// Speech bubbles: a companion says something in a balloon over its head, like Clippy's pale
// yellow one ("It looks like you're writing a letter..."), with an optional list of choices,
// each with a blue bullet. The text is a small pixel font, so it looks the same on every
// computer and in the gallery.
//
//   const b = await speak(g, 'Would you like help?', { options: ['Yes', 'No thanks'] });
//   ...                        // the bubble stays over its head while it does other things
//   await b.close();
//   await say(g, 'Hello!', 2000);   // shows it for 2 s
//   await say(g, '{name}?', 1100, { vars: { name } });   // {name} filled in, in every language
//
// A bubble is a prop owned by the speaker, so it goes away when its action is interrupted. It
// sits above the speaker's head, or beside it when there's no room above, and stays on screen.
//
// Companions speak the language in use (engine/i18n.js): a bubble's text and choices are written
// in English, and each language's table has them under 'say.' and the English as written
// ('say.Ho ho ho!'), which tools/strings.mjs finds in the say() and speak() calls. A text that
// reaches them from somewhere else (a list of greetings, a table by companion) is marked where
// it's written with saying('...'); a text drawn some other way than in a bubble goes through
// spoken('...'). Write the words out in the call: a text put together in code (`${name}?`) can't
// be translated, so it takes {name} and vars instead.

import { env, clamp, wait, animate } from './core.js';
import { props } from './stage.js';
import { drawText, textWidth, reach, canWrite } from './pixelfont.js';
import { tr, fill, language, SPOKEN } from './i18n.js';

const INK = '#1E1E24', EDGE = '#3A3A44', FILL = '#FFFFE1', BULLET = '#2F6FD6';
const LINE = 7;                     // font pixels from one line to the next
const PAD = 3;                      // from the edge to the text
const MAX = 15;                     // letters on a line, at most
const BULLET_W = 5;                 // a choice's bullet and the space after it
const TAIL = 3;                     // how far the tail reaches out of the box

// What a companion says, in the language in use, {name} and the like filled in from vars. In a
// language the pixel font can't write (SPOKEN), or a text of it the font can't, it stays English.
export function spoken(text, vars) {
  const english = String(text ?? '');
  if (SPOKEN.has(language())) {
    const said = tr('say.' + english, english, vars);
    if (canWrite(said)) return said;
  }
  return fill(english, vars);
}

// Marks words a companion says that are kept away from the say() or speak() that says them (a
// list to pick from, a table by companion), so tools/strings.mjs finds them. Returns them as they are.
export const saying = text => text;

// Splits text into lines of at most MAX letters, at spaces (not at no-break ones: « Oui ! »).
function wrap(text) {
  const lines = [];
  let line = '';
  for (const word of text.toUpperCase().split(/[^\S  ]+/).filter(Boolean)) {
    if (line && (line + ' ' + word).length > MAX) { lines.push(line); line = word; } else line = line ? line + ' ' + word : word;
  }
  if (line) lines.push(line);
  return lines;
}

// Where the lines go, in font pixels: the first one's top (its capitals'), from one line to the
// next, and the box's size. Accents reach over the capitals and a cedilla under them, so a bubble
// with them keeps an empty row between its lines and its edge a little further off.
function layout(lines) {
  let up = 0, down = 0;
  for (const l of lines) { const r = reach(l.t); up = Math.max(up, r.up); down = Math.max(down, r.down); }
  const top = Math.max(PAD, 2 + up), pitch = Math.max(LINE, 6 + up + down);
  const w = Math.max(...lines.map(l => (l.bullet ? BULLET_W : 0) + textWidth(l.t))) + 2 * PAD;
  const h = top + (lines.length - 1) * pitch + 5 + Math.max(PAD - 1, down + 1) + 1;
  return { w, h, top, pitch };
}

// The bubble for `me` saying `text`, with `options` (choices, each on its own line with a
// bullet), both in the language in use. It's a prop, drawn in front of everyone: add it to props
// to show it. `alpha` fades it.
export function bubbleOf(me, text, { options = [], vars } = {}) {
  const lines = [...wrap(spoken(text, vars)).map(t => ({ t, bullet: false })), ...options.map(o => ({ t: spoken(o, vars).toUpperCase(), bullet: true }))];
  const box = layout(lines);
  return { kind: 'speech', owner: me, alpha: 0, drawFront: () => drawBubble(me, lines, box) };
}

// Drawn in font pixels of u screen pixels (sized to the screen's companions, so it stays readable
// over a small one sitting on text): a pale yellow box with rounded corners and a thin dark edge,
// and a tail that points at the speaker's head.
function drawBubble(me, lines, { w, h, top, pitch }, { ctx = env.ctx, u = Math.max(1, Math.floor(env.S * 0.45)), width = env.W, height = env.H } = {}) {
  const b = me.bbox;
  if (!b || me.alpha <= 0) return;
  const W = w * u, H = h * u, reach = (TAIL + 1) * u, headX = (b.l + b.r) / 2;
  // Above its head if it fits, else beside it (on the side with more room), level with its head.
  const above = b.t - H - reach >= 2, right = width - b.r > b.l;
  const x = Math.round(clamp(above ? headX - W / 2 : right ? b.r + reach : b.l - reach - W, 2, Math.max(2, width - W - 2)));
  const y = Math.round(clamp(above ? b.t - H - reach : b.t, 2, Math.max(2, height - H - 2)));
  const rect = (c, r, cw, ch, color) => { ctx.fillStyle = color; ctx.fillRect(x + c * u, y + r * u, cw * u, ch * u); };
  // The box: the edge with its corners cut and rounded inside, and the fill. Nothing is drawn twice,
  // so it fades in and out evenly.
  rect(1, 0, w - 2, 1, EDGE); rect(1, h - 1, w - 2, 1, EDGE); rect(0, 1, 1, h - 2, EDGE); rect(w - 1, 1, 1, h - 2, EDGE);
  for (const [c, r] of [[1, 1], [w - 2, 1], [1, h - 2], [w - 2, h - 2]]) rect(c, r, 1, 1, EDGE);
  rect(2, 1, w - 4, 1, FILL); rect(1, 2, w - 2, h - 4, FILL); rect(2, h - 2, w - 4, 1, FILL);
  // The tail: a wedge out of one edge that narrows to a point, its outer side straight. Each
  // step k is a slice 4 - k cells long, edge-colored at both ends, starting `start` cells along.
  const wedge = (cellAt, at, toward) => {
    for (let k = 0; k <= TAIL; k++) {
      const n = 4 - k, start = toward < 0 ? at : at + k;
      for (let j = 0; j < n; j++) cellAt(k, start + j, j === 0 || j === n - 1 ? EDGE : FILL);
    }
  };
  if (above) {
    // Out of the bottom edge, leaning toward the head.
    const toward = headX < x + W / 2 ? -1 : 1, tip = Math.round((headX - x) / u);
    wedge((k, c, color) => rect(c, h - 1 + k, 1, 1, color), clamp(toward < 0 ? tip : tip - 3, 2, w - 6), toward);
  } else {
    // Out of the side facing it, near the top, pointing at its head.
    const left = x > b.l, row = clamp(Math.round((b.t - y) / u) + 1, 2, h - 6);
    wedge((k, r, color) => rect(left ? -k : w - 1 + k, r, 1, 1, color), row, -1);
  }
  // The text, and the choices with their bullets.
  lines.forEach((line, i) => {
    const r0 = top + i * pitch;
    let c0 = PAD;
    if (line.bullet) { rect(c0 + 1, r0 + 1, 1, 3, BULLET); rect(c0, r0 + 2, 3, 1, BULLET); c0 += BULLET_W; }
    drawText(ctx, line.t, x + c0 * u, y + r0 * u, u, INK);
  });
}

// The same bubble on a canvas of its own, its tail down near the middle, for a host's clickable
// bubble (a page's own "Customize us!" button: its text as it is, not translated). The canvas is
// sized to fit at u screen pixels per font pixel and dpr device pixels per screen pixel; returns
// its size in screen pixels.
export function paintBubble(canvas, text, u = 2, dpr = 1) {
  const lines = wrap(text).map(t => ({ t, bullet: false })), box = layout(lines);
  const width = box.w * u + 4, height = (box.h + TAIL + 1) * u + 4;
  canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, width, height); ctx.imageSmoothingEnabled = false;
  // A stand-in speaker just under the canvas's bottom middle puts the bubble at (2, 2), tail down.
  const speaker = { alpha: 1, bbox: { l: width / 2, r: width / 2, t: height - 2, b: height } };
  drawBubble(speaker, lines, box, { ctx, u, width, height });
  return { width, height };
}

// Pops a bubble up over its head and returns it, with close() to take it away again.
export async function speak(g, text, opts) {
  const bubble = bubbleOf(g.me, text, opts);
  props.add(bubble);
  await animate(140, g, t => { bubble.alpha = t; });
  bubble.close = async () => {
    await animate(160, g, t => { bubble.alpha = 1 - t; });
    props.delete(bubble);
  };
  return bubble;
}

// Says it for ms, then the bubble goes.
export async function say(g, text, ms, opts) {
  const bubble = await speak(g, text, opts);
  await wait(ms, g);
  await bubble.close();
}
